import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Sunrise, Smartphone, Waves, BookOpen, Home, Anchor } from 'lucide-react';
import { CtaButton } from './CtaButton';
export function ProductReveal() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const pillars = [{
    number: 1,
    icon: Sunrise,
    name: 'La Rutina de Calma',
    result: 'Una secuencia de mañana y noche (5+10 min) que le señala a tu sistema nervioso que estás a salvo — desde que abres los ojos hasta que los cierras'
  }, {
    number: 2,
    icon: Smartphone,
    name: 'Corte de Estimulantes',
    result: 'El protocolo de desintoxicación digital de 90 segundos que resetea tu córtex prefrontal y dispara la producción natural de melatonina'
  }, {
    number: 3,
    icon: Waves,
    name: 'Reemplazo Somático',
    result: 'La Sacudida — el mismo mecanismo que usan los animales para liberar trauma — adaptada para liberar el cortisol atrapado en tus músculos en 3 minutos'
  }, {
    number: 4,
    icon: BookOpen,
    name: 'La Caja de Rumiación',
    result: 'El Brain Dump 3x3: cierra las 20 pestañas mentales que consumen tu energía cada noche y le demuestra a tu cerebro que puede soltar lo que lleva reteniendo'
  }, {
    number: 5,
    icon: Home,
    name: 'Tu Santuario de Sueño',
    result: 'La auditoría de 4 variables de entorno (temperatura, oscuridad, ruido, atmósfera) que puede mejorar tu sueño un 30-40% sin comprar nada nuevo'
  }, {
    number: 6,
    icon: Anchor,
    name: 'El Ancla de Calma',
    result: 'La técnica de anclaje neurosomático que usan negociadores de rehenes y cirujanos para mantener la calma bajo presión extrema — instalada en 14 días'
  }];
  return <section ref={ref} className="w-full px-6 py-10" data-id="element-214">
      <div className="max-w-4xl mx-auto" data-id="element-215">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6
      }} data-id="element-216">
          <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-center" data-id="element-217">
            Presentando:{' '}
            <span className="text-amber-gold" data-id="element-218">NEURO RESET Somático</span>
          </h2>

          <p className="text-xl md:text-2xl text-center mb-12 text-warm-cream/80" data-id="element-219">
            El Kit de Rescate de 15 Minutos para Apagar la Ansiedad y Dormir
            Profundamente
          </p>

          <div className="space-y-6 mb-16" data-id="element-220">
            <p className="text-lg md:text-xl" data-id="element-221">
              NEURO RESET Somático no es otro libro de autoayuda.
            </p>
            <p className="text-lg md:text-xl" data-id="element-222">
              No es teoría. No es información para acumular y olvidar.
            </p>
            <p className="text-lg md:text-xl" data-id="element-223">
              Es un manual de trabajo activo — un botiquín de primeros auxilios
              para tu sistema nervioso — diseñado para darte resultados desde la
              primera noche en que lo uses.
            </p>
            <p className="text-lg md:text-xl" data-id="element-224">
              Cada herramienta está construida sobre un principio único: actuar
              directamente sobre tu biología, no sobre tus pensamientos.
            </p>
            <p className="text-lg md:text-xl font-medium text-amber-gold" data-id="element-225">
              Porque cuando el sistema nervioso recibe las señales correctas, la
              calma no es un esfuerzo. Es la respuesta natural.
            </p>
          </div>

          <h3 className="font-playfair text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-226">
            Los 6 Pilares del Reset Neurológico
          </h3>

          <div className="grid gap-6" data-id="element-227">
            {pillars.map((pillar, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-warm-cream/5 border border-muted-teal/20 rounded-lg p-6 hover:bg-warm-cream/10 transition-colors" data-id="element-228">
                <div className="flex items-start gap-4" data-id="element-229">
                  <div className="flex-shrink-0" data-id="element-230">
                    <div className="w-12 h-12 rounded-full bg-muted-teal/20 flex items-center justify-center" data-id="element-231">
                      <span className="text-2xl font-playfair font-bold text-muted-teal" data-id="element-232">
                        {pillar.number}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-233">
                    <div className="flex items-center gap-3 mb-3" data-id="element-234">
                      <pillar.icon className="w-6 h-6 text-muted-teal" strokeWidth={1.5} data-id="element-235" />
                      <h4 className="font-playfair text-xl md:text-2xl font-semibold" data-id="element-236">
                        {pillar.name}
                      </h4>
                    </div>
                    <p className="text-warm-cream/80" data-id="element-237">{pillar.result}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <CtaButton text="Quiero el kit completo + los dos bonos" subtitle="Incluye los 6 pilares + 2 bonos exclusivos" variant="secondary" data-id="element-238" />
        </motion.div>
      </div>
    </section>;
}